import { AGGREGATORS, CHART_AGGREGATORS, CHART_TYPE, DS_ID } from 'src/constants/chart';

export default function dsSessionData() {
  return {
    id: DS_ID.SESSION,
    label: this.$t('insights.chartConfigurator.sourcesSessionDataLabel'),
    hasDateRange: true,

    // True when program type does not have to be loaded separately for filtering
    dataContainsProgramType: false,
    dataContainsTargetStatus: false,

    availableFilters: ['programs', 'programTags', 'programTypes', 'targetStatuses', 'users'],

    groupByOptions: [
      {
        label: this.$t('insights.chartConfigurator.groupByNoGroupLabel'),
        value: null,
        seriesLabelFrom: null,
      },
      {
        label: this.$t('insights.chartConfigurator.groupByProgramLabel'),
        value: 'programId',
        seriesLabelFrom: 'programName',
        seriesOrderBy: 'programName',
      },
      {
        label: this.$t('insights.chartConfigurator.groupByTargetLabel'),
        value: 'targetId',
        seriesLabelFrom: 'targetLabel',
        seriesOrderBy: 'targetOrder',
      },
      {
        label: this.$t('insights.chartConfigurator.groupByPromptLevelLabel'),
        value: 'promptLevelId',
        seriesLabelFrom: 'promptLevelLabel',
        seriesOrderBy: 'promptLevelOrder',
      },
      {
        label: this.$t('insights.chartConfigurator.groupByUserLabel'),
        value: 'userId',
        seriesLabelFrom: 'userName',
        seriesOrderBy: 'userName',
      },
      {
        label: this.$t('insights.chartConfigurator.groupByCurrentPromptLevelLabel'),
        value: 'currentPromptLevelId',
        seriesLabelFrom: 'promptLevelLabel',
        seriesOrderBy: 'promptLevelOrder',
      },
      {
        label: this.$t('insights.chartConfigurator.groupByMode'),
        value: 'mode',
        seriesLabelFrom: 'modeLabel',
        seriesOrderBy: 'mode',
      },
    ],

    xAxes: [
      {
        id: 'xaDateDaily',
        label: this.$t('insights.chartConfigurator.xAxisDateDailyLabel'),
        chartType: CHART_TYPE.LINE,
        xAxisType: 'date',
        xAxisAggregateBy: 'sessionId',
        xAxisDataProperty: 'date',
        xAxisCategoryLabel: null,
        xAxisTimeUnit: 'day',
        xAxisOrderBy: 'date',
        xAxisSplitBy: null,
        disableChartTypes: [CHART_TYPE.SCATTERCHART],
      },
      {
        id: 'xaDateWeekly',
        chartType: CHART_TYPE.LINE,
        label: this.$t('insights.chartConfigurator.xAxisDateWeeklyLabel'),
        xAxisType: 'date',
        xAxisAggregateBy: 'sessionId',
        xAxisDataProperty: 'date',
        xAxisCategoryLabel: null,
        xAxisTimeUnit: 'week',
        xAxisOrderBy: 'date',
        xAxisSplitBy: null,
        disableChartTypes: [CHART_TYPE.SCATTERCHART],
      },
      {
        id: 'xaDateMonthly',
        chartType: CHART_TYPE.COLUMN,
        label: this.$t('insights.chartConfigurator.xAxisDateMonthlyLabel'),
        xAxisType: 'date',
        xAxisAggregateBy: 'sessionId',
        xAxisDataProperty: 'date',
        xAxisCategoryLabel: null,
        xAxisTimeUnit: 'month',
        xAxisOrderBy: 'date',
        xAxisSplitBy: null,
        disableChartTypes: [CHART_TYPE.SCATTERCHART],
      },
      {
        id: 'xaSession',
        label: this.$t('insights.chartConfigurator.xAxisSessionLabel'),
        chartType: CHART_TYPE.LINE,
        xAxisType: 'category',
        xAxisAggregateBy: 'sessionId',
        xAxisDataProperty: 'cumulativeSession',
        xAxisCategoryLabel: 'sessionDate',
        xAxisTimeUnit: null,
        xAxisOrderBy: 'date',
        xAxisSplitBy: 'sessionDateOnly',
      },
      {
        id: 'xaProgram',
        label: this.$t('insights.chartConfigurator.xAxisProgramLabel'),
        chartType: CHART_TYPE.COLUMN,
        xAxisType: 'category',
        xAxisAggregateBy: 'programId',
        xAxisDataProperty: 'programId',
        xAxisCategoryLabel: 'programName',
        xAxisTimeUnit: null,
        xAxisOrderBy: 'programName',
        xAxisSplitBy: null,
        disableChartTypes: [CHART_TYPE.SCATTERPLOT],
      },
      {
        id: 'xaTarget',
        label: this.$t('insights.chartConfigurator.xAxisTargetLabel'),
        chartType: CHART_TYPE.COLUMN,
        xAxisType: 'category',
        xAxisAggregateBy: 'targetId',
        xAxisDataProperty: 'targetId',
        xAxisCategoryLabel: 'targetLabel',
        xAxisTimeUnit: null,
        xAxisOrderBy: 'targetLabel',
        xAxisSplitBy: null,
        disableChartTypes: [CHART_TYPE.SCATTERPLOT],
      },
      {
        id: 'xaPromptLevel',
        label: this.$t('insights.chartConfigurator.xAxisPromptLevelLabel'),
        chartType: CHART_TYPE.COLUMN,
        xAxisType: 'category',
        xAxisAggregateBy: 'promptLevelId',
        xAxisDataProperty: 'promptLevelId',
        xAxisCategoryLabel: 'promptLevelLabel',
        xAxisTimeUnit: null,
        xAxisOrderBy: 'promptLevelLabel',
        xAxisSplitBy: null,
        disableChartTypes: [CHART_TYPE.SCATTERPLOT],
      },
      {
        id: 'xaUser',
        label: this.$t('insights.chartConfigurator.xAxisUserLabel'),
        chartType: CHART_TYPE.COLUMN,
        xAxisType: 'category',
        xAxisAggregateBy: 'userId',
        xAxisDataProperty: 'userId',
        xAxisCategoryLabel: 'userName',
        xAxisTimeUnit: null,
        xAxisOrderBy: 'userName',
        xAxisSplitBy: null,
        disableChartTypes: [CHART_TYPE.SCATTERPLOT],
      },
    ],

    seriesPresets: [
      {
        id: 'spPercentageGroup',
        label: this.$t('insights.chartConfigurator.seriesPercentage'),
        isGroupHeader: true,
      },
      {
        id: 'spSuccessRate',

        label: this.$t('measurementMetrics.percentCorrect.label'),
        dataProperty: 'scoreValue',
        aggregator: AGGREGATORS.WEIGHTED_AVERAGE,
        chartAggregator: CHART_AGGREGATORS.WEIGHTED_AVERAGE,
        chartType: CHART_TYPE.LINE,
        axis: 'percentage',
        axisCategory: null,
        axisCategoryLabel: null,
        hideColumnInTable: false,
        disableChartTypes: [CHART_TYPE.LOGARITHMIC],
      },
      {
        // TODO : explore why this returns infinity
        id: 'spPercentageOfTotal',

        label: this.$t('insights.chartConfigurator.seriesPercentageOfTotal'),
        dataProperty: 'scoreValue',
        aggregator: AGGREGATORS.PERCENTAGE,
        chartAggregator: CHART_AGGREGATORS.AVERAGE,
        chartType: CHART_TYPE.STACKED,
        axis: 'percentage',
        axisCategory: null,
        axisCategoryLabel: null,
        hideColumnInTable: false,
        disableChartTypes: [CHART_TYPE.LOGARITHMIC],
      },
      {
        id: 'spFrequencyGroup',
        label: this.$t('insights.chartConfigurator.seriesFrequency'),
        isGroupHeader: true,
      },
      {
        id: 'spSuccess',
        label: this.$t('insights.chartConfigurator.seriesSuccessfulObservations'),
        dataProperty: 'scoreValue',
        aggregator: AGGREGATORS.SUM,
        chartAggregator: CHART_AGGREGATORS.SUM,
        chartType: CHART_TYPE.COLUMN,
        axis: 'number',
        axisCategory: null,
        axisCategoryLabel: null,
        hideColumnInTable: false,
      },
      {
        id: 'spRateGroup',
        label: this.$t('insights.chartConfigurator.seriesRate'),
        isGroupHeader: true,
      },
      {
        id: 'spFrequencySuccessesPerMinute',

        label: this.$t('measurementMetrics.ratePerMinute.label'),
        dataProperty: 'scoreValue',
        frequencyScale: 60,
        frequencyDivident: 'scoreValue',
        frequencyDivisor: 'frequencyDuration',
        aggregator: AGGREGATORS.FREQUENCY,
        chartAggregator: CHART_AGGREGATORS.CUSTOM_FREQUENCY,
        chartType: CHART_TYPE.LINE,
        axis: 'frequencyMinute',
        axisCategory: null,
        axisCategoryLabel: null,
        hideColumnInTable: false,
      },
      {
        id: 'spFrequencySuccessesPerHour',

        label: this.$t('measurementMetrics.ratePerHour.label'),
        dataProperty: 'scoreValue',
        frequencyScale: 60 * 60,
        frequencyDivident: 'scoreValue',
        frequencyDivisor: 'frequencyDuration',
        aggregator: AGGREGATORS.FREQUENCY,
        chartAggregator: CHART_AGGREGATORS.CUSTOM_FREQUENCY,
        chartType: CHART_TYPE.LINE,
        axis: 'frequencyHour',
        axisCategory: null,
        axisCategoryLabel: null,
        hideColumnInTable: false,
      },
      {
        id: 'spFrequencySuccessesPerDay',

        label: this.$t('measurementMetrics.ratePerDay.label'),
        dataProperty: 'scoreValue',
        frequencyScale: 24 * 60 * 60,
        frequencyDivident: 'scoreValue',
        frequencyDivisor: 'frequencyDuration',
        aggregator: AGGREGATORS.FREQUENCY,
        chartAggregator: CHART_AGGREGATORS.CUSTOM_FREQUENCY,
        chartType: CHART_TYPE.LINE,
        axis: 'frequencyDay',
        axisCategory: null,
        axisCategoryLabel: null,
        hideColumnInTable: false,
      },
      {
        id: 'spDurationGroup',
        label: this.$t('insights.chartConfigurator.seriesDuration'),
        isGroupHeader: true,
      },
      {
        id: 'spTotalDuration',

        label: this.$t('measurementMetrics.totalDuration.label'),
        dataProperty: 'observationDuration',
        aggregator: AGGREGATORS.SUM,
        chartAggregator: CHART_AGGREGATORS.SUM,
        chartType: CHART_TYPE.LINE,
        axis: 'duration',
        axisCategory: null,
        axisCategoryLabel: null,
        hideColumnInTable: false,
        disableChartTypes: [CHART_TYPE.LOGARITHMIC],
      },
      {
        id: 'spMinDuration',

        label: this.$t('measurementMetrics.minDuration.label'),
        dataProperty: 'observationDuration',
        aggregator: AGGREGATORS.LOW,
        chartAggregator: CHART_AGGREGATORS.LOW,
        chartType: CHART_TYPE.LINE,
        axis: 'duration',
        axisCategory: null,
        axisCategoryLabel: null,
        hideColumnInTable: false,
        disableChartTypes: [CHART_TYPE.LOGARITHMIC],
      },
      {
        id: 'spMaxDuration',

        label: this.$t('measurementMetrics.maxDuration.label'),
        dataProperty: 'observationDuration',
        aggregator: AGGREGATORS.HIGH,
        chartAggregator: CHART_AGGREGATORS.HIGH,
        chartType: CHART_TYPE.LINE,
        axis: 'duration',
        axisCategory: null,
        axisCategoryLabel: null,
        hideColumnInTable: false,
        disableChartTypes: [CHART_TYPE.LOGARITHMIC],
      },
      {
        id: 'spAvgDuration',

        label: this.$t('measurementMetrics.avgDuration.label'),
        dataProperty: 'observationDuration',
        aggregator: AGGREGATORS.AVERAGE,
        chartAggregator: CHART_AGGREGATORS.AVERAGE,
        chartType: CHART_TYPE.LINE,
        axis: 'duration',
        axisCategory: null,
        axisCategoryLabel: null,
        hideColumnInTable: false,
        disableChartTypes: [CHART_TYPE.LOGARITHMIC],
      },
      {
        id: 'spRatingScaleGroup',
        label: this.$t('insights.chartConfigurator.seriesRatingScale'),
        isGroupHeader: true,
      },
      {
        id: 'spAverageRating',
        label: this.$t('insights.chartConfigurator.seriesRatingScaleLabel'),
        dataProperty: 'scoreValue',
        aggregator: AGGREGATORS.AVERAGE,
        chartAggregator: CHART_AGGREGATORS.AVERAGE,
        chartType: CHART_TYPE.LINE,
        axis: 'number',
        axisNumberDecimals: 2,
        axisCategory: null,
        axisCategoryLabel: null,
        hideColumnInTable: false,
        disableChartTypes: [CHART_TYPE.LOGARITHMIC],

        yAxisMin: 'custom',
        yAxisMax: 'custom',
        yStrictMinMax: true,
      },
      {
        id: 'spLearningOpportunitiesGroup',
        label: this.$t('insights.chartConfigurator.seriesLearningOpportunities'),
        isGroupHeader: true,
      },
      {
        id: 'spOccurrences',

        label: this.$t('insights.chartConfigurator.seriesOccurrences'),
        dataProperty: 'scoreValue',
        aggregator: AGGREGATORS.COUNT,
        chartAggregator: CHART_AGGREGATORS.SUM,
        chartType: CHART_TYPE.COLUMN,
        axis: 'number',
        axisCategory: null,
        axisCategoryLabel: null,
        hideColumnInTable: false,
        disableChartTypes: [CHART_TYPE.LOGARITHMIC],
      },
      {
        id: 'spFrequencyOccurencesPerMinute',

        label: this.$t('insights.chartConfigurator.seriesOccurrencesPM'),
        dataProperty: 'COUNT',
        countDataProperty: 'scoreValue',
        frequencyScale: 60,
        frequencyDivident: 'COUNT',
        frequencyDivisor: 'frequencyDuration',
        aggregator: AGGREGATORS.FREQUENCY,
        chartAggregator: CHART_AGGREGATORS.CUSTOM_FREQUENCY,
        chartType: CHART_TYPE.LINE,
        axis: 'frequencyMinute',
        axisCategory: null,
        axisCategoryLabel: null,
        hideColumnInTable: false,
        disableChartTypes: [CHART_TYPE.LOGARITHMIC],
      },
      {
        id: 'spFrequencyOccurencesPerHour',

        label: this.$t('insights.chartConfigurator.seriesOccurrencesPH'),
        dataProperty: 'COUNT',
        countDataProperty: 'scoreValue',
        frequencyScale: 60 * 60,
        frequencyDivident: 'COUNT',
        frequencyDivisor: 'frequencyDuration',
        aggregator: AGGREGATORS.FREQUENCY,
        chartAggregator: CHART_AGGREGATORS.CUSTOM_FREQUENCY,
        chartType: CHART_TYPE.LINE,
        axis: 'frequencyHour',
        axisCategory: null,
        axisCategoryLabel: null,
        hideColumnInTable: false,
        disableChartTypes: [CHART_TYPE.LOGARITHMIC],
      },
      {
        id: 'spFrequencyOccurencesPerDay',

        label: this.$t('insights.chartConfigurator.seriesOccurrencesPD'),
        dataProperty: 'COUNT',
        countDataProperty: 'scoreValue',
        frequencyScale: 24 * 60 * 60,
        frequencyDivident: 'COUNT',
        frequencyDivisor: 'frequencyDuration',
        aggregator: AGGREGATORS.FREQUENCY,
        chartAggregator: CHART_AGGREGATORS.CUSTOM_FREQUENCY,
        chartType: CHART_TYPE.LINE,
        axis: 'frequencyDay',
        axisCategory: null,
        axisCategoryLabel: null,
        hideColumnInTable: false,
        disableChartTypes: [CHART_TYPE.LOGARITHMIC],
      },
      {
        id: 'spTrialsPerSessionHour',
        label: this.$t('insights.chartConfigurator.seriesTrialsPerSessionHour'),
        dataProperty: 'COUNT',
        countDataProperty: 'scoreValue',
        frequencyScale: 60 * 60,
        frequencyDivident: 'COUNT',
        frequencyDivisor: 'sessionDuration',
        frequencyDivisorDistinctBy: 'sessionId',
        aggregator: AGGREGATORS.FREQUENCY,
        chartAggregator: CHART_AGGREGATORS.CUSTOM_FREQUENCY,
        chartType: CHART_TYPE.LINE,
        axis: 'frequencyHour',
        axisCategory: null,
        axisCategoryLabel: null,
        hideColumnInTable: false,
        disableChartTypes: [CHART_TYPE.LOGARITHMIC],
      },
      {
        id: 'spPromptFadingGroup',
        label: this.$t('insights.chartConfigurator.seriesPromptFading'),
        isGroupHeader: true,
      },
      {
        id: 'spPromptSuccessRate',
        label: this.$t('insights.chartConfigurator.seriesPromptSuccessRate'),
        dataProperty: 'promptLevelScoreValue',
        aggregator: AGGREGATORS.AVERAGE,
        chartAggregator: CHART_AGGREGATORS.AVERAGE,
        chartType: CHART_TYPE.LINE,
        axis: 'percentage',
        axisCategory: null,
        axisCategoryLabel: null,
        hideColumnInTable: false,
        disableChartTypes: [CHART_TYPE.LOGARITHMIC],
      },
    ],

    tableColumns: [
      {
        name: 'date',
        field: 'date',
        format: (val) => (val ? this.getLocaleDateString(val) : ''),
        label: this.$t('insights.chartConfigurator.columnsDate'),
        align: 'left',
        sortable: true,
        visibility: [null, 'sessionId', 'date'],
        visibilityExport: ['sessionId', 'date'],
      },
      {
        name: 'session',
        field: 'session',
        label: this.$t('insights.chartConfigurator.columnsSession'),
        align: 'left',
        sortable: true,
        visibility: ['sessionId', 'date'],
        visibilityExport: ['sessionId', 'date'],
      },
      {
        name: 'programName',
        field: 'programName',
        label: this.$t('insights.chartConfigurator.columnsProgram'),
        align: 'left',
        sortable: true,
        classes: 'insights-truncate-label',
        visibility: ['programId', 'targetId'],
        visibilityExport: ['programId', 'targetId'],
      },
      {
        name: 'targetLabel',
        field: 'targetLabel',
        label: this.$t('insights.chartConfigurator.columnsTarget'),
        align: 'left',
        sortable: true,
        classes: 'insights-truncate-label',
        visibility: ['targetId'],
        visibilityExport: ['targetId'],
      },
      {
        name: 'promptLevelLabel',
        field: 'promptLevelLabel',
        label: this.$t('insights.chartConfigurator.columnsPromptLevel'),
        align: 'left',
        sortable: true,
        visibility: ['promptLevelId'],
        visibilityExport: ['promptLevelId'],
      },
      {
        name: 'userName',
        field: 'userName',
        label: this.$t('insights.chartConfigurator.columnsUser'),
        align: 'left',
        sortable: true,
        visibility: ['userId'],
        visibilityExport: ['userId'],
      },
      {
        name: 'trials',
        field: 'trials',
        label: this.$t('insights.chartConfigurator.columnsTrials'),
        align: 'left',
        sortable: true,
        visibility: ['sessionId'],
        visibilityExport: ['sessionId'],
      },
      {
        name: 'minTrials',
        field: 'minTrials',
        label: this.$t('insights.chartConfigurator.columnsMinTrialCount'),
        align: 'left',
        sortable: true,
        visibility: ['sessionId'],
        visibilityExport: ['sessionId'],
      },
    ],

    additionalSettings: [
      {
        id: 'hideDeletedTargets',
        label: this.$t('insights.chartConfigurator.hideDeletedTargetsDataLabel'),
        value: false,
      },
      {
        id: 'hideBelowMinTrials',
        label: this.$t('insights.chartConfigurator.hideBelowMinTrials'),
        value: false,
      },
    ],
  };
}
